import { CommonModule } from '@angular/common';
import { Component, ElementRef, OnDestroy, OnInit, QueryList, ViewChildren } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { RevealedEnkashCard } from '@app-model/revealedEnkashCard.model';
import { EnkashCardsService } from '@app-service/enkash-cards.service';
import { UserService } from '@app-service/user.service';
import { IconComponent } from '@app/imprest-payments/admin-v2/components/icon/icon.component';
import { toTitleCase } from '@app/imprest-payments/admin-v2/utils/name-format.util';
import { Subscription } from 'rxjs';

type CardViewPhase = 'loading' | 'error' | 'otp' | 'card';
type RevealField = 'number' | 'cvv' | null;

const OTP_LENGTH = 6;
const RESEND_SECONDS = 30;
const REVEAL_SECONDS = 300;

@Component({
  selector: 'app-card-view',
  standalone: true,
  imports: [CommonModule, IconComponent],
  templateUrl: './card-view.component.html',
  styleUrl: './card-view.component.scss',
})
export class CardViewComponent implements OnInit, OnDestroy {
  @ViewChildren('otpCell') private otpCells?: QueryList<ElementRef<HTMLInputElement>>;

  phase: CardViewPhase = 'loading';
  errorMessage = '';

  private enKashCardId = '';
  private otpRequestId = '';

  otpValues: string[] = Array(OTP_LENGTH).fill('');
  otpError = '';
  otpShake = false;
  otpSubmitting = false;
  otpSending = false;
  maskedContact = '';
  resendSeconds = 0;
  private resendTimer: ReturnType<typeof setInterval> | null = null;

  revealedCard: RevealedEnkashCard | null = null;
  revealField: RevealField = null;
  revealSecondsLeft = 0;
  private revealTimer: ReturnType<typeof setInterval> | null = null;

  private readonly subscription = new Subscription();

  constructor(
    private readonly route: ActivatedRoute,
    private readonly enkashCardsService: EnkashCardsService,
    private readonly userService: UserService
  ) {
    this.userService.showHaderImage(false);
    this.userService.showHaderSetting(false);
    this.userService.hideSideNav(true);
  }

  ngOnInit(): void {
    this.subscription.add(
      this.route.queryParams.subscribe((params) => {
        const ekidParam = params['ekid'];
        this.enKashCardId = ekidParam ? this.resolveEnKashCardId(ekidParam) : '';

        if (!this.enKashCardId) {
          this.showError('This link is invalid or has expired.');
          return;
        }

        this.phase = 'loading';
        this.sendOtp();
      })
    );
  }

  private resolveEnKashCardId(raw: string): string {
    try {
      const decoded = atob(raw);
      const roundTrip = btoa(decoded).replace(/=+$/, '');
      const looksLikeId = /^[A-Za-z0-9_-]{4,}$/.test(decoded);
      if (looksLikeId && roundTrip === raw.replace(/=+$/, '')) {
        return decoded;
      }
    } catch {}
    return raw;
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
    this.clearResendTimer();
    this.clearRevealTimer();
  }

  retry(): void {
    this.phase = 'loading';
    this.sendOtp();
  }

  resendOtp(): void {
    if (this.resendSeconds > 0 || this.otpSending || this.otpSubmitting) {
      return;
    }
    this.otpSending = true;
    this.subscription.add(
      this.enkashCardsService.sendOtpForCardViewOpen(this.enKashCardId, 'VIEW').subscribe({
        next: (res: any) => {
          this.otpSending = false;
          if (!res?.otpRequestId) {
            this.otpError = 'Unable to resend the code. Please try again.';
            return;
          }
          this.otpRequestId = res.otpRequestId;
          this.resetOtpBoxes();
          this.startResendCooldown();
        },
        error: (err) => {
          this.otpSending = false;
          this.otpError = this.extractError(err, 'Unable to resend the code. Please try again.');
        },
      })
    );
  }

  onOtpDigit(index: number, rawValue: string): void {
    const digit = rawValue.replace(/\D/g, '').slice(-1);
    const next = [...this.otpValues];
    next[index] = digit;
    this.otpValues = next;
    this.otpError = '';
    if (digit && index < OTP_LENGTH - 1) {
      this.focusCell(index + 1);
    }
  }

  onOtpKeydown(index: number, event: KeyboardEvent): void {
    if (event.key === 'Backspace' && !this.otpValues[index] && index > 0) {
      this.focusCell(index - 1);
    }
  }

  onOtpPaste(event: ClipboardEvent): void {
    const text = (event.clipboardData?.getData('text') || '').replace(/\D/g, '').slice(0, OTP_LENGTH);
    if (!text) {
      return;
    }
    event.preventDefault();
    const next: string[] = Array(OTP_LENGTH).fill('');
    text.split('').forEach((char, i) => (next[i] = char));
    this.otpValues = next;
    this.focusCell(Math.min(text.length, OTP_LENGTH - 1));
  }

  get otpCode(): string {
    return this.otpValues.join('');
  }

  get verifyDisabled(): boolean {
    return this.otpCode.length !== OTP_LENGTH || this.otpSubmitting;
  }

  verifyOtp(): void {
    if (this.verifyDisabled) {
      return;
    }
    this.otpSubmitting = true;
    this.otpError = '';
    this.subscription.add(
      this.enkashCardsService.viewCardOpen(this.enKashCardId, this.otpRequestId, this.otpCode).subscribe({
        next: (res: any) => {
          this.otpSubmitting = false;
          const card = res?.payload;
          if (card?.cardNumber) {
            this.revealedCard = { ...card, cardHolderName: toTitleCase(card.cardHolderName || '') };
            this.phase = 'card';
            this.showField('number');
          } else {
            this.failOtp(res?.response_message || "That code didn't match. Please try again.");
          }
        },
        error: (err) => {
          this.otpSubmitting = false;
          this.failOtp(this.extractError(err, "That code didn't match. Please try again."));
        },
      })
    );
  }

  showField(which: RevealField): void {
    if (!this.revealedCard || !which) {
      return;
    }
    this.revealField = which;
    this.restartRevealTimer();
  }

  hideNow(): void {
    this.clearRevealTimer();
    this.revealField = null;
  }

  viewDetailsAgain(): void {
    this.revealedCard = null;
    this.revealField = null;
    this.clearRevealTimer();
    this.phase = 'otp';
    this.resetOtpBoxes();
    this.sendOtp();
  }

  get displayNumber(): string {
    if (this.revealField === 'number' && this.revealedCard) {
      return (this.revealedCard.cardNumber.match(/.{1,4}/g) || []).join(' ');
    }
    const last4 = this.revealedCard?.cardNumber?.slice(-4) || '';
    return `•••• •••• •••• ${last4}`;
  }

  get displayCvv(): string {
    return this.revealField === 'cvv' && this.revealedCard ? this.revealedCard.cvv : '•••';
  }

  get displayExpiry(): string {
    const expiry = this.revealedCard?.expiry || '';
    const parts = expiry.split('/');
    if (parts.length !== 2) {
      return expiry || '••/••';
    }
    return `${parts[0]}/${parts[1].slice(-2)}`;
  }

  get displayHolderName(): string {
    return this.revealedCard?.cardHolderName || '';
  }

  get formattedBalanceWhole(): string {
    const amount = this.revealedCard?.otbBalance || 0;
    const sign = amount < 0 ? '-' : '';
    const [whole] = Math.abs(amount).toFixed(2).split('.');
    return `${sign}₹${Number(whole).toLocaleString('en-IN')}`;
  }

  get formattedBalanceDecimal(): string {
    const [, decimal] = Math.abs(this.revealedCard?.otbBalance || 0).toFixed(2).split('.');
    return `.${decimal}`;
  }

  private sendOtp(): void {
    this.subscription.add(
      this.enkashCardsService.sendOtpForCardViewOpen(this.enKashCardId, 'VIEW').subscribe({
        next: (res: any) => {
          if (!res?.otpRequestId) {
            this.showError('Unable to send the verification code. Please try again.');
            return;
          }
          this.otpRequestId = res.otpRequestId;
          const mobile = res?.mobileNumber || '';
          this.maskedContact = mobile ? `+91 ${mobile}` : 'your registered mobile';
          this.phase = 'otp';
          this.resetOtpBoxes();
          this.startResendCooldown();
        },
        error: (err) => {
          this.showError(this.extractError(err, 'Unable to send the verification code. Please try again.'));
        },
      })
    );
  }

  private failOtp(message: string): void {
    this.otpError = message;
    this.otpShake = true;
    this.resetOtpBoxes();
    setTimeout(() => (this.otpShake = false), 400);
    this.focusCell(0);
  }

  private resetOtpBoxes(): void {
    this.otpValues = Array(OTP_LENGTH).fill('');
  }

  private focusCell(index: number): void {
    setTimeout(() => this.otpCells?.get(index)?.nativeElement.focus());
  }

  private startResendCooldown(): void {
    this.clearResendTimer();
    this.resendSeconds = RESEND_SECONDS;
    this.resendTimer = setInterval(() => {
      this.resendSeconds -= 1;
      if (this.resendSeconds <= 0) {
        this.clearResendTimer();
      }
    }, 1000);
  }

  private clearResendTimer(): void {
    if (this.resendTimer) {
      clearInterval(this.resendTimer);
      this.resendTimer = null;
    }
  }

  get revealTimeLabel(): string {
    const minutes = Math.floor(this.revealSecondsLeft / 60);
    const seconds = this.revealSecondsLeft % 60;
    return `${minutes}:${seconds < 10 ? '0' + seconds : seconds}`;
  }

  private restartRevealTimer(): void {
    this.clearRevealTimer();
    this.revealSecondsLeft = REVEAL_SECONDS;
    this.revealTimer = setInterval(() => {
      this.revealSecondsLeft -= 1;
      if (this.revealSecondsLeft <= 0) {
        this.hideNow();
      }
    }, 1000);
  }

  private clearRevealTimer(): void {
    if (this.revealTimer) {
      clearInterval(this.revealTimer);
      this.revealTimer = null;
    }
  }

  private showError(message: string): void {
    this.errorMessage = message;
    this.phase = 'error';
  }

  private extractError(err: any, fallback: string): string {
    return err?.error?.response_message || fallback;
  }
}
